import React from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Label,
  FormGroup,
  Badge,
} from 'reactstrap';
import { Formik, Form, Field } from 'formik';
import { useTranslation } from 'react-i18next';
import { capitalize } from '../../helpers/sharedFunction';
import { languageName } from '../../helpers/countryLanguage';

const SimulatorSetup = ({
  agent,
  country,
  acquisitionOrders,
  campaignsByOrderId,
  leadFields,
  onStart,
  loading,
}) => {
  const { t } = useTranslation();

  // firstName and phone have their own inputs below. They also exist as lead
  // fields, so an agent configured to collect one of them would wipe its default
  // here and render a second control under the same name. Dropping it loses
  // nothing: the value still reaches the lead from the dedicated input, and a
  // field the lead already carries is one the agent must not ask for anyway.
  const OWN_FIELDS = ['firstName', 'phone'];

  const collectFields = (agent.collectFields || [])
    .map((entry) => ({
      ...entry,
      field: leadFields.find((lf) => lf.id === entry.leadFieldId),
    }))
    .filter(({ field }) => !!field && !OWN_FIELDS.includes(field.name));

  const initialValues = {
    acquisitionOrderId: acquisitionOrders.length ? acquisitionOrders[0].id : '',
    mode: '',
    // Empty, like every other field: the fake lead has no country of its own
    // to borrow a name or a prefix from, and the server fills a placeholder
    // phone when none is given.
    firstName: '',
    phone: '',
    ...collectFields.reduce(
      (acc, { field }) => ({ ...acc, [field.name]: '' }),
      {},
    ),
  };

  if (!acquisitionOrders.length)
    return (
      <Card>
        <CardBody>
          {t(
            'No acquisition order routes to this agent: there is nothing to simulate yet.',
          )}
        </CardBody>
      </Card>
    );

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={({ acquisitionOrderId, mode, firstName, phone, ...fields }) =>
        onStart({
          acquisitionOrderId,
          mode: mode || undefined,
          lead: {
            // Only what the colleague actually filled in: an empty string
            // would read as "known and blank" and stop the agent asking.
            ...Object.entries({ firstName, phone, ...fields }).reduce(
              (acc, [key, value]) =>
                String(value).trim() ? { ...acc, [key]: value } : acc,
              {},
            ),
          },
        })
      }
    >
      {({ values }) => (
        <Form>
          <Card>
            <CardBody>
              <h5 className="mb-3">{t('What to simulate')}</h5>
              {/*
                The agent speaks the language of the country and keeps its
                clock: said here, so whoever plays the lead knows which
                language to answer in and which hours the agent reasons on.
              */}
              <p className="text-muted font-size-12">
                {t('Language')}:{' '}
                <strong>
                  {languageName(country?.languageTag) || t('not set')}
                </strong>
                {' · '}
                {t('Time zone')}: <strong>{country?.timezone || '—'}</strong>
              </p>
              <FormGroup>
                <Label>{t('Acquisition Order')}</Label>
                <Field
                  as="select"
                  name="acquisitionOrderId"
                  className="form-control"
                >
                  {acquisitionOrders.map((order) => (
                    <option key={order.id} value={order.id}>
                      {order.name}
                    </option>
                  ))}
                </Field>
                <small className="text-muted">
                  {(campaignsByOrderId[values.acquisitionOrderId] || []).length
                    ? `${t('Campaigns')}: ${(
                        campaignsByOrderId[values.acquisitionOrderId] || []
                      )
                        .map((campaign) => campaign.name)
                        .join(', ')}`
                    : t('No campaign attached')}
                </small>
              </FormGroup>
              <FormGroup>
                <Label>{t('Routing mode')}</Label>
                <Field as="select" name="mode" className="form-control">
                  <option value="">{t('As configured on the order')}</option>
                  <option value="autonomous">{t('Autonomous')}</option>
                  <option value="prequalify">{t('Pre-qualify')}</option>
                </Field>
              </FormGroup>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <h5 className="mb-1">{t('The lead you will play')}</h5>
              <p className="text-muted">
                {t(
                  'Leave a field empty to arrive without it. Fill it to arrive from a form with it already known: the agent must not ask for it again.',
                )}
              </p>
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label>{t('First name')}</Label>
                    <Field
                      name="firstName"
                      className="form-control"
                      placeholder={t('unknown')}
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>{t('Phone')}</Label>
                    <Field
                      name="phone"
                      className="form-control"
                      placeholder={t('unknown')}
                    />
                  </FormGroup>
                </Col>
                {collectFields.map(({ field, required }) => (
                  <Col md={6} key={field.id}>
                    <FormGroup>
                      <Label>
                        {/*
                          A lead field's `name` is a translation key: the
                          shared label does `capitalize(t(label || name))`
                          (lib/form-field/Label.js), and the Italian locale
                          defines `occupation`, `birthday` and the rest.
                          Rendered raw it showed a colleague the developer
                          identifiers while every other screen showed the
                          Italian ones.
                        */}
                        {capitalize(t(field.name))}{' '}
                        {required && (
                          <Badge color="light">{t('Required')}</Badge>
                        )}
                      </Label>
                      {field.typeField === 'select' ? (
                        <Field
                          as="select"
                          name={field.name}
                          className="form-control"
                        >
                          <option value="">{t('unknown')}</option>
                          {/* listValues are {title, value} objects, not
                              strings — see InputLead.js and the way
                              FormLeadField writes them. */}
                          {(Array.isArray(field.listValues)
                            ? field.listValues
                            : []
                          ).map(({ title, value }) => (
                            <option key={value} value={value}>
                              {t(title)}
                            </option>
                          ))}
                        </Field>
                      ) : (
                        <Field name={field.name} className="form-control" />
                      )}
                    </FormGroup>
                  </Col>
                ))}
              </Row>
              <Button color="primary" type="submit" disabled={loading}>
                {loading ? t('Starting…') : t('Start the conversation')}
              </Button>
            </CardBody>
          </Card>
        </Form>
      )}
    </Formik>
  );
};

export default SimulatorSetup;
